// File: src/pages/candidate/ExamPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// --- ICONS SVG ---
const IconCode = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>;
const IconTimer = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
const IconArrowBack = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>;
const IconArrowForward = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>;
const IconCheckCircle = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-primary"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>;
const IconVideoCam = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z"></path><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>;
const IconCheckSmall = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
const IconAlert = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;

export default function ExamPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    // --- STATE ---
    const [testData, setTestData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(0);
    const [jobId, setJobId] = useState(null);

    // --- ANTI-CHEAT STATE ---
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [violationCount, setViolationCount] = useState(0);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');

    // Refs
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const lastViolationTime = useRef(0);
    const startTimeRef = useRef(new Date());
    const attemptIdRef = useRef(`attempt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        if (location.state?.jobId) {
            setJobId(location.state.jobId);
            localStorage.setItem('currentJobId', location.state.jobId);
        } else {
            const savedJobId = localStorage.getItem('currentJobId');
            if (savedJobId) setJobId(savedJobId);
        }
    }, [location.state]);

    // 1. FETCH DATA
    useEffect(() => {
        const fetchTest = async () => {
            const token = localStorage.getItem('skillmatch_token');
            try {
                const res = await fetch(`${apiBaseUrl}/api/public/tests/${id}`, {
                    headers: { 'x-auth-token': token }
                });

                if (res.status === 403) {
                    const data = await res.json();
                    alert(data.message || "Bạn đã hoàn thành bài thi này!");
                    localStorage.removeItem('currentJobId');
                    navigate('/candidate/test-history');
                    return;
                }

                if (!res.ok) throw new Error('Không thể tải bài thi');

                const data = await res.json();
                setTestData(data);
                setTimeLeft(data.timeLimit * 60);
                startTimeRef.current = new Date();
            } catch (error) {
                console.error("Lỗi:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchTest();
    }, [id, apiBaseUrl, navigate]);

    // 2. LOGIC ANTI-CHEAT (Lock Browser)
    useEffect(() => {
        if (loading || isSubmitting || !testData?.securitySettings?.lockBrowser) return;

        const handleViolation = (msg) => {
            const now = Date.now();
            if (now - lastViolationTime.current < 1000) return;

            if (!isSubmitting) {
                lastViolationTime.current = now;
                recordViolation(msg);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) handleViolation("Cảnh báo: Bạn đã rời khỏi màn hình thi!");
        };

        const handleBlur = () => {
            handleViolation("Cảnh báo: Mất tập trung vào bài thi (Click ra ngoài).");
        };

        const preventDefault = (e) => e.preventDefault();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("blur", handleBlur);
        document.addEventListener('contextmenu', preventDefault);
        document.addEventListener('copy', preventDefault);
        document.addEventListener('paste', preventDefault);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("blur", handleBlur);
            document.removeEventListener('contextmenu', preventDefault);
            document.removeEventListener('copy', preventDefault);
            document.removeEventListener('paste', preventDefault);
        };
    }, [loading, testData, isSubmitting]);

    const recordViolation = async (msg) => {
        const limit = testData?.securitySettings?.maxViolationLimit || 3;
        const token = localStorage.getItem('skillmatch_token');

        setViolationCount(prev => {
            const newCount = prev + 1;
            if (newCount >= limit) {
                handleSubmit(true);
            }
            return newCount;
        });
        setWarningMessage(msg);
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 3000);

        try {
            await fetch(`${apiBaseUrl}/api/public/tests/${id}/violation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify({
                    attemptId: attemptIdRef.current,
                    message: msg,
                    type: 'BROWSER_TAB',
                    startTime: startTimeRef.current,
                    jobId: jobId
                })
            });
        } catch (err) { console.error("Log violation failed:", err); }
    };

    // 3. LOGIC WEBCAM SNAPSHOT
    const captureAndUploadSnapshot = async () => {
        if (!videoRef.current || isSubmitting) return;
        const token = localStorage.getItem('skillmatch_token') || localStorage.getItem('token');
        try {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
            const imageBase64 = canvas.toDataURL('image/jpeg', 0.5);

            await fetch(`${apiBaseUrl}/api/public/tests/${id}/snapshot`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token
                },
                body: JSON.stringify({ image: imageBase64, attemptId: attemptIdRef.current, startTime: startTimeRef.current })
            });
        } catch (err) {
            console.error("Snapshot failed:", err);
        }
    };

    useEffect(() => {
        if (loading || !testData?.securitySettings?.enableWebcam) return;

        const startCamera = async () => {
            try {
                if (!streamRef.current) {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                    streamRef.current = stream;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = streamRef.current;
                    videoRef.current.play().catch(e => console.log("Autoplay blocked:", e));
                }
            } catch (err) {
                console.error("Camera error:", err);
                alert("Vui lòng cấp quyền Camera để làm bài thi này!");
            }
        };
        startCamera();

        const intervalMinutes = testData.securitySettings.webcamInterval || 2;
        const intervalMs = intervalMinutes * 60 * 1000;

        const snapshotTimer = setInterval(() => {
            captureAndUploadSnapshot();
        }, intervalMs);

        return () => clearInterval(snapshotTimer);
    }, [loading, testData]);

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
            }
        };
    }, []);

    // 4. TIMER
    useEffect(() => {
        if (loading) return;
        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 0) {
                    clearInterval(timer);
                    handleSubmit(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [loading]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // --- HANDLERS ---
    const handleSelectAnswer = (optionIndex) => {
        setAnswers(prev => ({ ...prev, [currentQIndex]: optionIndex }));
    };

    const handleTextAnswer = (e) => {
        const text = e.target.value;
        setAnswers(prev => ({ ...prev, [currentQIndex]: text }));
    };

    const handleNext = () => {
        if (currentQIndex < testData.questions.length - 1) setCurrentQIndex(currentQIndex + 1);
    };

    const handlePrev = () => {
        if (currentQIndex > 0) setCurrentQIndex(currentQIndex - 1);
    };

    // SUBMIT
    const handleSubmit = async (forced = false) => {
        const isForced = typeof forced === 'boolean' ? forced : false;
        setIsSubmitting(true);
        const token = localStorage.getItem('skillmatch_token') || localStorage.getItem('token');

        try {
            const res = await fetch(`${apiBaseUrl}/api/public/tests/${id}/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token
                },
                body: JSON.stringify({
                    answers,
                    startTime: startTimeRef.current,
                    attemptId: attemptIdRef.current,
                    isTerminated: isForced,
                    jobId: jobId
                })
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.message || 'Lỗi khi nộp bài');
            }

            const result = await res.json();

            navigate('/candidate/test-result', {
                state: {
                    score: isForced ? 0 : result.score,
                    totalQuestions: result.totalQuestions,
                    correctCount: isForced ? 0 : result.correctCount,
                    violationCount: violationCount,
                    isTerminated: isForced,
                    skillAnalysis: result.skillAnalysis || [],
                    aiFeedback: result.aiFeedback || "Chưa có dữ liệu phân tích.",
                    aiSuggestion: result.aiSuggestion || "",

                    testName: testData.assessmentName,
                    testType: testData.testType, // 'mcq', 'code', 'essay'...
                    questions: testData.questions, // Truyền danh sách câu hỏi
                    userAnswers: answers
                },
                replace: true
            });
            localStorage.removeItem('currentJobId');

        } catch (error) {
            console.error("Lỗi nộp bài:", error);
            alert("Có lỗi kết nối. Vui lòng kiểm tra mạng và thử lại.");
            setIsSubmitting(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center text-gray-500">Đang tải đề thi...</div>;
    if (!testData || !testData.questions) return (
        <div className="h-screen flex flex-col items-center justify-center gap-4 bg-[#F6F7F8]">
            <div className="text-red-500 font-medium text-lg">⚠️ Không thể tải dữ liệu bài thi</div>
            <p className="text-gray-500">Có thể bạn đã mất kết nối hoặc bài thi không tồn tại.</p>
            <button onClick={() => navigate(-1)} className="px-4 py-2 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50">
                Quay lại
            </button>
        </div>
    );

    const currentQuestion = testData.questions[currentQIndex];
    const progressPercent = (Object.keys(answers).length / testData.questions.length) * 100;
    const violationLimit = testData?.securitySettings?.maxViolationLimit || 3;

    // PHÂN LOẠI CÂU HỎI
    const qType = currentQuestion.type ? currentQuestion.type.toLowerCase() : 'mcq';
    const isCodeQuestion = qType === 'code' || qType === 'practical';
    const isEssayQuestion = qType === 'essay' || qType === 'write';
    const isMCQ = !isCodeQuestion && !isEssayQuestion;

    return (
        <div className="h-screen flex flex-col bg-[#F6F7F8] font-sans text-[#121417] overflow-hidden select-none" onContextMenu={(e) => e.preventDefault()}>

            {showWarning && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
                        <IconAlert />
                        <div>
                            <p className="font-bold">CẢNH BÁO VI PHẠM ({violationCount}/{violationLimit})</p>
                            <p className="text-sm">{warningMessage}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <header className="h-20 bg-white border-b border-[#dde1e4] px-6 flex items-center justify-between shrink-0 z-20 shadow-sm relative">
                <div className="flex items-center gap-3 w-1/4">
                    <div className="w-10 h-10 rounded bg-[#eff6ff] flex items-center justify-center text-[#2563EB]"><IconCode /></div>
                    <div>
                        <h1 className="text-lg font-bold leading-tight truncate">{testData.assessmentName}</h1>
                        <p className="text-xs text-[#637588]">SkillMatch Assessment</p>
                    </div>
                </div>

                <div className="flex flex-col gap-2 w-1/3 max-w-md">
                    <div className="flex justify-between items-end px-1">
                        <span className="text-sm font-medium">Tiến độ</span>
                        <span className="text-sm font-semibold text-[#2563EB]">{Object.keys(answers).length}/{testData.questions.length}</span>
                    </div>
                    <div className="h-2.5 w-full bg-[#dde1e4] rounded-full overflow-hidden">
                        <div className="h-full bg-[#2563EB] rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 w-1/4">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${timeLeft < 300 ? 'bg-red-50 border-red-100 text-red-600' : 'bg-blue-50 border-blue-100 text-blue-600'}`}>
                        <span className="animate-pulse"><IconTimer /></span>
                        <span className="text-xl font-bold tracking-wider">{formatTime(timeLeft)}</span>
                    </div>
                </div>
            </header>

            {/* MAIN CONTENT */}
            <main className="flex flex-1 overflow-hidden">
                <div className="flex-1 flex flex-col h-full bg-[#F6F7F8] relative">

                    {/* SCROLLABLE QUESTION AREA */}
                    <div className="flex-1 overflow-y-auto p-8">
                        <div className="max-w-4xl mx-auto w-full">

                            {/* BADGE LOẠI CÂU HỎI */}
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eff6ff] text-[#2563EB] text-sm font-semibold mb-6">
                                <span>Câu hỏi số {currentQIndex + 1}</span>
                                <span className="w-1 h-1 rounded-full bg-[#2563EB]"></span>
                                <span>
                                    {isCodeQuestion ? 'Lập trình / Code' : (isEssayQuestion ? 'Tự luận' : 'Trắc nghiệm')}
                                </span>
                            </div>

                            {/* QUESTION TITLE */}
                            <h2 className="text-2xl md:text-3xl font-bold text-[#121417] leading-tight mb-6">
                                {currentQuestion.question || currentQuestion.title}
                            </h2>

                            {/* HIỂN THỊ MEDIA */}
                            {(currentQuestion.mediaUrl || currentQuestion.mediaType) && currentQuestion.mediaUrl && (
                                <div className="mb-8 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white p-2">
                                    {currentQuestion.mediaType === 'audio' || currentQuestion.mediaUrl.endsWith('.mp3') ? (
                                        <div className="p-4 flex items-center justify-center bg-gray-50 rounded-lg">
                                            <audio controls src={currentQuestion.mediaUrl} className="w-full max-w-md" />
                                        </div>
                                    ) : (
                                        <img
                                            src={currentQuestion.mediaUrl}
                                            alt="Question Media"
                                            className="max-h-96 w-full object-contain rounded-lg mx-auto"
                                        />
                                    )}
                                </div>
                            )}

                            {/* PROMPT */}
                            {currentQuestion.prompt && (
                                <div className="bg-white p-6 rounded-xl border border-gray-200 mb-8 text-gray-700 whitespace-pre-wrap leading-relaxed shadow-sm">
                                    {currentQuestion.prompt}
                                </div>
                            )}

                            {/* RENDER GIAO DIỆN THEO LOẠI CÂU HỎI */}

                            {/* 1. CODE QUESTION */}
                            {isCodeQuestion && (
                                <div className="w-full">
                                    {/* Sample Code / Boilerplate Display */}
                                    {currentQuestion.sampleCode && (
                                        <div className="mb-6">
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wide">
                                                Code mẫu / Boilerplate:
                                            </label>
                                            <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 font-mono text-sm text-gray-700 overflow-x-auto">
                                                <pre>{currentQuestion.sampleCode}</pre>
                                            </div>
                                        </div>
                                    )}

                                    {/* ✅ FIX CODE EDITOR: Dùng '!' (important) để ghi đè mọi style mặc định */}
                                    <label className="block text-sm font-bold text-gray-700 mb-2">
                                        Viết code của bạn tại đây:
                                    </label>
                                    <textarea
                                        className="w-full p-5 rounded-xl min-h-[400px] font-mono text-sm leading-relaxed resize-y shadow-inner outline-none border border-[#333] placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 
                                                   !bg-[#1E1E1E] !text-[#D4D4D4] focus:!bg-[#1E1E1E] focus:!text-[#D4D4D4]"
                                        placeholder="// Viết giải pháp của bạn ở đây..."
                                        value={answers[currentQIndex] || ''}
                                        onChange={handleTextAnswer}
                                        spellCheck={false}
                                    ></textarea>
                                    <p className="text-xs text-gray-500 mt-2 text-right">
                                        {answers[currentQIndex] ? answers[currentQIndex].length : 0} ký tự
                                    </p>
                                </div>
                            )}

                            {/* 2. ESSAY QUESTION */}
                            {isEssayQuestion && (
                                <div className="w-full">
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Câu trả lời của bạn:</label>
                                    <textarea
                                        className="w-full p-5 border border-gray-300 rounded-xl h-60 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none text-base leading-relaxed resize-none shadow-inner bg-white text-gray-900"
                                        placeholder="Nhập nội dung bài làm tại đây..."
                                        value={answers[currentQIndex] || ''}
                                        onChange={handleTextAnswer}
                                    ></textarea>
                                    <p className="text-xs text-gray-500 mt-2 text-right">
                                        {answers[currentQIndex] ? answers[currentQIndex].length : 0} ký tự
                                    </p>
                                </div>
                            )}

                            {/* 3. MCQ QUESTION */}
                            {isMCQ && (
                                <div className="grid grid-cols-1 gap-4 mb-10">
                                    {currentQuestion.options && currentQuestion.options.map((option, idx) => {
                                        const isSelected = answers[currentQIndex] === idx;
                                        return (
                                            <label
                                                key={idx}
                                                onClick={() => handleSelectAnswer(idx)}
                                                className={`group relative flex items-center p-5 rounded-xl border-2 shadow-sm cursor-pointer transition-all duration-200
                                                    ${isSelected ? 'bg-[#eff6ff] border-[#2563EB]' : 'bg-white border-transparent hover:border-[#2563EB]/50 hover:shadow-md'}
                                                `}
                                            >
                                                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold mr-5 transition-colors ${isSelected ? 'bg-[#2563EB] text-white' : 'bg-gray-100 text-gray-500'}`}>
                                                    {String.fromCharCode(65 + idx)}
                                                </div>
                                                <div className="flex-1">
                                                    <p className={`text-lg font-medium transition-colors ${isSelected ? 'text-[#2563EB]' : 'text-[#121417]'}`}>
                                                        {option}
                                                    </p>
                                                </div>
                                                {isSelected && <div className="absolute right-5 top-1/2 -translate-y-1/2 text-[#2563EB]"><IconCheckCircle /></div>}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* FIXED FOOTER */}
                    <div className="shrink-0 bg-white border-t border-[#dde1e4] p-6 z-10">
                        <div className="max-w-4xl mx-auto flex justify-between items-center">
                            <button onClick={handlePrev} disabled={currentQIndex === 0} className={`flex items-center gap-2 px-6 py-3 rounded-lg border border-[#dde1e4] font-medium transition-colors ${currentQIndex === 0 ? 'opacity-50 cursor-not-allowed bg-gray-50 text-gray-400' : 'hover:bg-gray-50 text-[#121417]'}`}>
                                <IconArrowBack /> Câu trước
                            </button>
                            <button onClick={handleNext} disabled={currentQIndex === testData.questions.length - 1} className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg transition-all transform ${currentQIndex === testData.questions.length - 1 ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-[#2563EB] text-white hover:bg-blue-700 hover:-translate-y-0.5 shadow-blue-500/20'}`}>
                                Câu tiếp theo <IconArrowForward />
                            </button>
                        </div>
                    </div>
                </div>

                {/* SIDEBAR */}
                <aside className="w-80 bg-white border-l border-[#dde1e4] flex flex-col shrink-0 h-full overflow-hidden">
                    <div className="p-5 border-b border-[#dde1e4]">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-[#121417] flex items-center gap-2"><span className="text-[#637588]"><IconVideoCam /></span> Giám sát camera</h3>
                            {testData.securitySettings?.enableWebcam && <span className="flex h-2 w-2 relative"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span></span>}
                        </div>
                        <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-900 shadow-inner group">
                            {testData.securitySettings?.enableWebcam ? (
                                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover transform scale-x-[-1]" />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-500 text-xs">Camera tắt</div>
                            )}
                            {testData.securitySettings?.enableWebcam && (
                                <div className="absolute top-2 right-2 bg-red-600/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span> REC</div>
                            )}
                        </div>
                        {testData.securitySettings?.enableWebcam && <p className="text-xs text-[#637588] mt-2 text-center">Tự động chụp {testData.securitySettings.webcamInterval} phút/lần</p>}
                    </div>

                    <div className="flex-1 p-5 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-[#121417]">Danh sách câu hỏi</h3>
                            <span className="text-xs text-[#637588]">{testData.questions.length} câu</span>
                        </div>
                        <div className="grid grid-cols-5 gap-2.5">
                            {testData.questions.map((q, idx) => {
                                const isAnswered = answers[idx] !== undefined && answers[idx] !== "";
                                const isCurrent = currentQIndex === idx;
                                let btnClass = "aspect-square flex items-center justify-center rounded-lg font-medium text-sm border transition-colors ";
                                if (isCurrent) btnClass += "bg-[#2563EB] text-white font-bold ring-2 ring-blue-200 border-[#2563EB]";
                                else if (isAnswered) btnClass += "bg-green-100 text-green-700 border-transparent hover:bg-green-200";
                                else btnClass += "bg-gray-50 text-[#637588] border-[#dde1e4] hover:border-[#2563EB] hover:text-[#2563EB]";
                                return <button key={idx} onClick={() => setCurrentQIndex(idx)} className={btnClass}>{isAnswered && !isCurrent ? <IconCheckSmall /> : idx + 1}</button>;
                            })}
                        </div>
                    </div>

                    <div className="p-5 border-t border-[#dde1e4] bg-gray-50">
                        <button onClick={() => handleSubmit(false)} className="w-full py-3 rounded-lg border-2 border-[#2563EB] text-[#2563EB] font-bold hover:bg-[#2563EB] hover:text-white transition-colors">Nộp bài thi</button>
                    </div>
                </aside>
            </main>
        </div>
    );
}